import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import type { QcLot, QcResult } from '@alpha-edge/shared'
import { api } from '../../services/api'
import { useModal } from '../../hooks/use-modal'
import {
  PageHeader,
  FormField,
  Alert,
  Modal,
  StatusBadge,
  card,
  btnPrimary,
  btnSecondary,
  colors,
} from '../../components/ui'

const CHART_WIDTH = 700
const CHART_HEIGHT = 250
const CHART_PADDING = 40

function LeveyJenningsChart({
  results,
  targetValue,
  targetSd,
}: {
  readonly results: readonly QcResult[]
  readonly targetValue: number
  readonly targetSd: number
}) {
  if (results.length === 0)
    return <p style={{ color: colors.textMuted, fontSize: '0.875rem' }}>No QC results recorded yet.</p>

  const sorted = [...results].reverse()
  const maxPoints = Math.min(sorted.length, 30)
  const visible = sorted.slice(-maxPoints)

  const yMin = targetValue - 4 * targetSd
  const yMax = targetValue + 4 * targetSd
  const yRange = yMax - yMin

  const xStep = (CHART_WIDTH - 2 * CHART_PADDING) / Math.max(visible.length - 1, 1)
  const toY = (val: number) => CHART_PADDING + ((yMax - val) / yRange) * (CHART_HEIGHT - 2 * CHART_PADDING)
  const toX = (i: number) => CHART_PADDING + i * xStep

  const sdLines = [-3, -2, -1, 0, 1, 2, 3].map((n) => ({
    value: targetValue + n * targetSd,
    label: n === 0 ? 'Mean' : `${n > 0 ? '+' : ''}${n}SD`,
    color:
      Math.abs(n) === 3 ? colors.danger : Math.abs(n) === 2 ? colors.warning : n === 0 ? colors.primary : colors.border,
    dash: Math.abs(n) >= 2 ? '4,4' : '',
  }))

  return (
    <svg
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      style={{
        width: '100%',
        maxWidth: CHART_WIDTH,
        border: `1px solid ${colors.border}`,
        borderRadius: '6px',
        background: '#fff',
      }}
    >
      {sdLines.map((line) => {
        const y = toY(line.value)
        return (
          <g key={line.label}>
            <line
              x1={CHART_PADDING}
              y1={y}
              x2={CHART_WIDTH - CHART_PADDING}
              y2={y}
              stroke={line.color}
              strokeWidth={1}
              strokeDasharray={line.dash}
            />
            <text x={CHART_PADDING - 4} y={y + 3} textAnchor="end" fontSize="9" fill={colors.textMuted}>
              {line.label}
            </text>
          </g>
        )
      })}

      {visible.length > 1 && (
        <polyline
          points={visible.map((r, i) => `${toX(i)},${toY(r.measuredValue)}`).join(' ')}
          fill="none"
          stroke={colors.primary}
          strokeWidth={1.5}
        />
      )}

      {visible.map((r, i) => {
        const cx = toX(i)
        const cy = toY(r.measuredValue)
        const fill = r.status === 'fail' ? colors.danger : r.status === 'warning' ? colors.warning : colors.success
        return <circle key={r.id} cx={cx} cy={cy} r={4} fill={fill} stroke="#fff" strokeWidth={1.5} />
      })}
    </svg>
  )
}

export function QcLotDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [lot, setLot] = useState<QcLot | null>(null)
  const [results, setResults] = useState<readonly QcResult[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const recordModal = useModal()
  const [measuredValue, setMeasuredValue] = useState('')
  const [comment, setComment] = useState('')
  const [recording, setRecording] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get<{ lot: QcLot; results: readonly QcResult[] }>(`/qc/lots/${id}/results`)
      if (res.data) {
        setLot(res.data.lot)
        setResults(res.data.results)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleRecord = async () => {
    if (!measuredValue) return
    setRecording(true)
    setError(null)
    try {
      await api.post('/qc/results', {
        lotId: id,
        measuredValue: parseFloat(measuredValue),
        comment: comment || undefined,
      })
      setMeasuredValue('')
      setComment('')
      recordModal.close()
      await fetchData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record')
    } finally {
      setRecording(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>
  if (!lot) return <div>QC Lot not found.</div>

  return (
    <div>
      <PageHeader
        title={`QC Lot: ${lot.lotNumber}`}
        subtitle={`${lot.analyte} (${lot.testType})`}
        backTo="/qc/lots"
        backLabel="Back to Lots"
        actions={
          <>
            {lot.status === 'active' && (
              <button onClick={recordModal.open} style={btnPrimary}>
                Record QC Result
              </button>
            )}
            <button onClick={() => navigate('/qc/lots')} style={btnSecondary}>
              Back to QC Lots
            </button>
          </>
        }
      />
      {error && <Alert type="error" message={error} />}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <div style={card}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Lot Details</h3>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: '0.5rem 1rem',
              margin: 0,
              fontSize: '0.875rem',
            }}
          >
            <dt style={{ color: colors.textMuted }}>Status</dt>
            <dd style={{ margin: 0 }}>
              <StatusBadge status={lot.status} />
            </dd>
            <dt style={{ color: colors.textMuted }}>Level</dt>
            <dd style={{ margin: 0 }}>
              <StatusBadge status={lot.level} />
            </dd>
            <dt style={{ color: colors.textMuted }}>Target</dt>
            <dd style={{ margin: 0 }}>
              {lot.targetValue} {lot.unit ?? ''}
            </dd>
            <dt style={{ color: colors.textMuted }}>SD</dt>
            <dd style={{ margin: 0 }}>{lot.targetSd}</dd>
            <dt style={{ color: colors.textMuted }}>Manufacturer</dt>
            <dd style={{ margin: 0 }}>{lot.manufacturer ?? '--'}</dd>
            <dt style={{ color: colors.textMuted }}>Expires</dt>
            <dd style={{ margin: 0 }}>
              {lot.expirationDate ? new Date(lot.expirationDate).toLocaleDateString() : '--'}
            </dd>
          </dl>
        </div>

        <div style={card}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Summary</h3>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: '0.5rem 1rem',
              margin: 0,
              fontSize: '0.875rem',
            }}
          >
            <dt style={{ color: colors.textMuted }}>Total Runs</dt>
            <dd style={{ margin: 0 }}>{results.length}</dd>
            <dt style={{ color: colors.textMuted }}>Pass</dt>
            <dd style={{ margin: 0, color: colors.success }}>{results.filter((r) => r.status === 'pass').length}</dd>
            <dt style={{ color: colors.textMuted }}>Warning</dt>
            <dd style={{ margin: 0, color: colors.warning }}>{results.filter((r) => r.status === 'warning').length}</dd>
            <dt style={{ color: colors.textMuted }}>Fail</dt>
            <dd style={{ margin: 0, color: colors.danger }}>{results.filter((r) => r.status === 'fail').length}</dd>
          </dl>
        </div>
      </div>

      <div style={card}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Levey-Jennings Chart</h3>
        <LeveyJenningsChart results={results} targetValue={lot.targetValue} targetSd={lot.targetSd} />
      </div>

      <div style={{ ...card, marginTop: '1rem' }}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Recent Results</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>Date</th>
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>Value</th>
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>SD from Mean</th>
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>Status</th>
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>Rule</th>
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>Comment</th>
            </tr>
          </thead>
          <tbody>
            {results.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: '1rem', color: colors.textMuted, textAlign: 'center' }}>
                  No results recorded.
                </td>
              </tr>
            ) : (
              results.map((r) => (
                <tr key={r.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: '0.5rem' }}>{new Date(r.performedAt).toLocaleString()}</td>
                  <td style={{ padding: '0.5rem' }}>{r.measuredValue}</td>
                  <td style={{ padding: '0.5rem' }}>{r.sdFromMean.toFixed(2)}</td>
                  <td style={{ padding: '0.5rem' }}>
                    <StatusBadge status={r.status} />
                  </td>
                  <td style={{ padding: '0.5rem' }}>{r.violationRule ?? '--'}</td>
                  <td style={{ padding: '0.5rem' }}>{r.comment ?? '--'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={recordModal.isOpen} onClose={recordModal.close} title="Record QC Result">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ margin: 0, fontSize: '0.875rem', color: colors.textMuted }}>
            Target: {lot.targetValue} +/- {lot.targetSd} {lot.unit ?? ''}
          </p>
          <FormField
            name="measuredValue"
            label="Measured Value"
            required
            type="number"
            value={measuredValue}
            onChange={setMeasuredValue}
            placeholder={`Expected: ${lot.targetValue}`}
          />
          <FormField name="comment" label="Comment (optional)" multiline value={comment} onChange={setComment} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button style={btnSecondary} onClick={recordModal.close}>
              Cancel
            </button>
            <button style={btnPrimary} onClick={handleRecord} disabled={recording || !measuredValue}>
              {recording ? 'Recording...' : 'Record Result'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
